import React, { useCallback, useEffect, useRef, useState } from 'react';
import { notesApi } from '@/lib/notesApi';
import { prepareAttachmentFile, ATTACH_MAX_BYTES, ATTACH_CAP_LABEL } from '@/lib/imageCompress';
import { getNotesModuleCaps } from '@/lib/attachmentCaps';
import {
  Paperclip, Plus, Download, Trash2, Loader2, FileText, ImageIcon, Eye, X,
} from 'lucide-react';

const ACCEPT = 'image/png,image/jpeg,image/gif,image/webp,application/pdf';

function formatSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
const isImage = (ct) => typeof ct === 'string' && ct.startsWith('image/');

// ---------------------------------------------------------------------------
// Note attachments — upload (image / PDF), inline preview, view + download.
// Files are proxied through our authenticated backend (no public URLs), so
// every fetch uses the notesApi client (token attached automatically) and we
// render images from object URLs created from the fetched blob.
// ---------------------------------------------------------------------------
export default function NoteAttachments({ noteId, initial = [], onChange, readOnly = false, onOpenViewer }) {
  const [list, setList] = useState(initial);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [lightbox, setLightbox] = useState(null); // { url, filename }
  const [thumbs, setThumbs] = useState({}); // id -> objectURL (images only)
  const inputRef = useRef(null);
  const urlsRef = useRef([]); // track object URLs for cleanup
  // Attachment cap (KB) for the Notes module — Super-Admin configurable.
  const [capBytes, setCapBytes] = useState(ATTACH_MAX_BYTES);
  const [capLabel, setCapLabel] = useState(ATTACH_CAP_LABEL);
  useEffect(() => {
    let alive = true;
    getNotesModuleCaps()
      .then((caps) => { if (alive && caps?.notes_kb) { setCapBytes(Number(caps.notes_kb) * 1024); setCapLabel(`${Number(caps.notes_kb)} KB`); } })
      .catch(() => { /* keep defaults */ });
    return () => { alive = false; };
  }, []);

  const track = (url) => { urlsRef.current.push(url); return url; };
  useEffect(() => () => { urlsRef.current.forEach((u) => URL.revokeObjectURL(u)); }, []);

  const fetchBlob = useCallback(async (att) => {
    const { data } = await notesApi.get(`/notes/notes/${noteId}/attachments/${att.id}`, { responseType: 'blob' });
    return track(URL.createObjectURL(data));
  }, [noteId]);

  // Lazy-load thumbnails for image attachments.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const att of list) {
        if (isImage(att.content_type) && !thumbs[att.id]) {
          try {
            const url = await fetchBlob(att);
            if (cancelled) return;
            setThumbs((prev) => ({ ...prev, [att.id]: url }));
          } catch { /* ignore individual thumb failures */ }
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, fetchBlob]);

  const commit = (next) => { setList(next); onChange?.(next); };

  const handleFiles = useCallback(async (files) => {
    if (!files || !files.length) return;
    setError('');
    setUploading(true);
    let next = list;
    for (const raw of Array.from(files)) {
      let file;
      try {
        // Auto-compress oversized images; block oversized PDFs.
        file = await prepareAttachmentFile(raw, capBytes);
      } catch (err) {
        if (err?.code === 'pdf_too_large') {
          setError(`“${raw.name}” is ${formatSize(err.size)} — PDFs must be ${capLabel} or smaller. Please compress it and re-upload.`);
        } else {
          setError(`“${raw.name}” (${formatSize(err?.size || raw.size)}) couldn’t be compressed under ${capLabel} — please use a smaller image.`);
        }
        continue;
      }
      try {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await notesApi.post(`/notes/notes/${noteId}/attachments`, fd);
        next = [...next, data];
        setList(next);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Upload failed.');
      }
    }
    onChange?.(next);
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
  }, [list, noteId, onChange, capBytes, capLabel]);

  const view = useCallback(async (att) => {
    // Preferred: open in the split-screen viewer beside the note.
    if (onOpenViewer) { onOpenViewer(att); return; }
    // Fallback (standalone use): image lightbox / new-tab for PDFs.
    try {
      setBusyId(att.id);
      const url = thumbs[att.id] || await fetchBlob(att);
      setLightbox(isImage(att.content_type) ? { url, filename: att.filename, image: true } : null);
      if (!isImage(att.content_type)) window.open(url, '_blank', 'noopener');
    } catch {
      setError('Could not open the file.');
    } finally { setBusyId(null); }
  }, [thumbs, fetchBlob, onOpenViewer]);

  const download = useCallback(async (att) => {
    try {
      setBusyId(att.id);
      const { data } = await notesApi.get(`/notes/notes/${noteId}/attachments/${att.id}`, {
        params: { download: 1 }, responseType: 'blob',
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.filename || 'file';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch {
      setError('Could not download the file.');
    } finally { setBusyId(null); }
  }, [noteId]);

  const remove = useCallback(async (att) => {
    try {
      setBusyId(att.id);
      await notesApi.delete(`/notes/notes/${noteId}/attachments/${att.id}`);
      commit(list.filter((a) => a.id !== att.id));
    } catch {
      setError('Could not remove the attachment.');
    } finally { setBusyId(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, noteId]);

  return (
    <div className="mt-4 rounded-2xl border border-[var(--n-line)] bg-[var(--n-surface-2)] p-3.5" data-testid="note-attachments">
      <div className="mb-2.5 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[13px] font-semibold text-[var(--n-ink-soft)]">
          <Paperclip size={14} className="text-[var(--n-accent)]" />
          Attachments{list.length ? ` (${list.length})` : ''}
        </div>
        <input ref={inputRef} type="file" accept={ACCEPT} multiple className="hidden"
          onChange={(e) => handleFiles(e.target.files)} data-testid="note-attachment-input" />
        {!readOnly && (
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
          data-testid="note-attachment-add-button"
          className="n-focus inline-flex items-center gap-1.5 rounded-lg border border-[var(--n-border)] px-2.5 py-1.5 text-[12px] font-semibold text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)] disabled:opacity-60 transition-colors">
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          Add file
        </button>
        )}
      </div>

      {error && <p className="mb-2 text-[12px] text-[var(--n-danger)]" data-testid="note-attachment-error">{error}</p>}

      {list.length === 0 ? (
        readOnly ? (
          <div className="rounded-xl border border-dashed border-[var(--n-border)] px-4 py-5 text-center text-[12px] text-[var(--n-muted-2)]" data-testid="note-attachment-empty-readonly">
            No attachments.
          </div>
        ) : (
        <button type="button" onClick={() => inputRef.current?.click()}
          className="n-focus flex w-full flex-col items-center gap-1.5 rounded-xl border border-dashed border-[var(--n-border)] px-4 py-6 text-center text-[var(--n-muted)] hover:bg-[var(--n-canvas-2)] transition-colors"
          data-testid="note-attachment-empty">
          <Paperclip size={18} className="text-[var(--n-muted-2)]" />
          <span className="text-[12.5px]">Drop nothing here — click to add an image or PDF</span>
          <span className="text-[11px] text-[var(--n-muted-2)]">Images auto-shrink to ≤{capLabel} · PDFs must be ≤{capLabel}</span>
        </button>
        )
      ) : (
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3" data-testid="note-attachment-list">
          {list.map((att) => (
            <div key={att.id} className="group relative overflow-hidden rounded-xl border border-[var(--n-line)] bg-[var(--n-surface)]" data-testid="note-attachment-item">
              <button type="button" onClick={() => view(att)}
                className="block w-full text-left"
                title={isImage(att.content_type) ? 'View image' : 'Open PDF'}>
                <div className="flex h-24 w-full items-center justify-center bg-[var(--n-canvas-2)]">
                  {isImage(att.content_type) ? (
                    thumbs[att.id] ? (
                      <img src={thumbs[att.id]} alt={att.filename} className="h-full w-full object-cover" />
                    ) : (
                      <Loader2 size={16} className="animate-spin text-[var(--n-muted-2)]" />
                    )
                  ) : (
                    <FileText size={26} className="text-[var(--n-accent)]" />
                  )}
                </div>
              </button>
              {/* Hover actions */}
              <div className="absolute right-1.5 top-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button type="button" onClick={() => view(att)} title="View" data-testid="note-attachment-view"
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-[var(--n-surface)]/90 text-[var(--n-ink-soft)] shadow-[var(--n-shadow)] hover:text-[var(--n-accent)]">
                  {busyId === att.id ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />}
                </button>
                <button type="button" onClick={() => download(att)} title="Download" data-testid="note-attachment-download"
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-[var(--n-surface)]/90 text-[var(--n-ink-soft)] shadow-[var(--n-shadow)] hover:text-[var(--n-accent)]">
                  <Download size={13} />
                </button>
                {!readOnly && (
                <button type="button" onClick={() => remove(att)} title="Remove" data-testid="note-attachment-delete"
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-[var(--n-surface)]/90 text-[var(--n-ink-soft)] shadow-[var(--n-shadow)] hover:text-[var(--n-danger)]">
                  <Trash2 size={13} />
                </button>
                )}
              </div>
              <div className="flex items-center gap-1.5 px-2.5 py-2">
                {isImage(att.content_type)
                  ? <ImageIcon size={12} className="shrink-0 text-[var(--n-muted-2)]" />
                  : <FileText size={12} className="shrink-0 text-[var(--n-muted-2)]" />}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[12px] font-medium text-[var(--n-ink)]" title={att.filename}>{att.filename}</div>
                  <div className="text-[10.5px] text-[var(--n-muted-2)]">{formatSize(att.size)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Image lightbox */}
      {lightbox?.image && (
        <div className="fixed inset-0 z-[250] flex items-center justify-center p-6" data-testid="note-attachment-lightbox" onClick={() => setLightbox(null)}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-[2px]" />
          <button className="absolute right-5 top-5 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20" onClick={() => setLightbox(null)} aria-label="Close">
            <X size={18} />
          </button>
          <img src={lightbox.url} alt={lightbox.filename} className="relative z-[1] max-h-[85vh] max-w-full rounded-lg object-contain shadow-2xl" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
